/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { HELP_CENTER_URL } from '../config/helpCenter';
import BugReportModal from './BugReportModal';
import { useTranslation } from 'react-i18next';
import { navigateToSubscription } from '../analytics';
import './Sidebar.css';
import { useAuth } from '../context/AuthContext';
import { FeatureFlag, useFeatureFlags } from '../contexts/FeatureFlagsContext';
import { useSafeTwilioConversations, AppConversation } from '../contexts/TwilioConversationsContext';
import { useAssociationContext } from '../hooks/useAssociationContext';
import { useBoardingModuleVisible } from '../hooks/useBoardingModuleVisible';
import { useProCountryFeatures } from '../hooks/useProCountryFeatures';
import { useIOSReaderMode } from '../hooks/useIOSReaderMode';
import axios from 'axios';
import Logo from './Logo/Logo';
import ProSidebarPlanCta, { ProSidebarPlanStatus } from './ProSidebarPlanCta';
import { isValidOnboardingTimestamp } from '../utils/onboardingStorage';
import { useOnboardingCoreProgress } from '../hooks/useOnboardingCoreProgress';
import { isFinanceAdminUser } from '../utils/financeAdmin';
import { canAccessMarketingTools } from '../constants/marketingToolsAllowlist';
import { canAccessFollowEngine } from '../constants/followEngineAllowlist';
import { configService } from '../services/configService';
import { canAccessClubSubscriptionsUi } from '../features/clubSubscriptionsAccess';
import { canShowPremiumUpsell } from '../utils/proPremiumUpsell';
import { isPremiumSidebarNav } from '../utils/visibleSidebarNav';
import { OFFERS_PRODUCT_PATHS } from '../pages/ForfaitsPages/offersProductView';
import { preloadRoute } from '../routes/lazyRoutes';

import {
  AccountBalanceOutlined,
  Apartment,
  Apps,
  ArrowBack,
  ArrowDropDown,
  ArrowDropUp,
  AttachMoney,
  AutorenewOutlined,
  BugReportOutlined,
  CalendarMonth,
  Campaign,
  AccountBalanceWalletOutlined,
  CardGiftcard,
  CreditCard,
  DescriptionOutlined,
  GroupAdd,
  Groups,
  HelpOutline,
  Home,
  HomeOutlined,
  LocalOffer,
  Menu,
  MenuBook,
  MenuOpen,
  SmsOutlined,
  PersonOutline,
  Pets,
  RocketLaunch,
  Send,
  Settings,
  Share,
  ShowChart,
  StarBorder,
  Storefront,
  VerifiedUser,
  WorkspacePremium,
} from '@mui/icons-material';
/**
 * Helper utilisé sur tous les NavLink pour précharger le chunk JS de la
 * route cible dès que l'utilisateur survole ou focus le lien (MT-5).
 *
 * - Délai 0 : `preloadRoute` est mémoïsé, les appels successifs sont gratuits.
 * - Pas de pré-chargement sur mobile : l'utilisateur tape directement sans hover,
 *   et on évite de saturer son forfait data.
 */
const buildPreloadHandlers = (path: string, isMobile: boolean) => {
  if (isMobile) return undefined;
  const handler = () => preloadRoute(path);
  return { onMouseEnter: handler, onFocus: handler };
};

function SidebarPremiumMark({ label }: { label: string }) {
  return (
    <span className="sidebar-premium-mark" title={label}>
      <WorkspacePremium className="sidebar-premium-mark__icon" aria-hidden />
      <span className="sr-only">{label}</span>
    </span>
  );
}


const SIDEBAR_COLLAPSED_STORAGE_KEY = 'rintintin-sidebar-collapsed';

type SidebarAccordionSection = 'essential' | 'offers' | 'business' | 'profile';

const BUSINESS_SECTION_PATHS = [
  '/app/billing',
  '/app/quotes',
  '/app/comptabilite',
  '/app/stats',
  '/app/finance',
] as const;

const resolveAccordionSectionForPath = (
  pathname: string
): SidebarAccordionSection | null => {
  if (
    pathname.startsWith('/app/planning') ||
    pathname.startsWith('/app/services') ||
    pathname.startsWith('/app/pension')
  ) {
    return 'essential';
  }
  if (Object.values(OFFERS_PRODUCT_PATHS).some((path) => pathname.startsWith(path))) {
    return 'offers';
  }
  if (BUSINESS_SECTION_PATHS.some((path) => pathname.startsWith(path))) {
    return 'business';
  }
  return null;
};

export const readSidebarCollapsedPreference = (): boolean => {
  try {
    return localStorage.getItem(SIDEBAR_COLLAPSED_STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
};

export const writeSidebarCollapsedPreference = (collapsed: boolean): void => {
  try {
    localStorage.setItem(SIDEBAR_COLLAPSED_STORAGE_KEY, String(collapsed));
  } catch {
    // ignore quota / private mode
  }
};

interface SidebarProps {
  /** Repli desktop (géré par PrivateLayout) */
  desktopCollapsed?: boolean;
  onToggleDesktopCollapse?: () => void;
  /** Menu rendu dans le Drawer MUI mobile — pas le layout desktop fixe */
  variant?: 'desktop' | 'drawer';
}

const Sidebar: React.FC<SidebarProps> = ({
  desktopCollapsed = false,
  onToggleDesktopCollapse,
  variant = 'desktop',
}) => {
  const { t } = useTranslation();
  const location = useLocation();
  const { user } = useAuth();
  const showPremiumMarks = !!user && canShowPremiumUpsell(user.is_premium);
  const premiumMarkLabel = t('sidebar:plan.premiumBadge');
  const showFinanceAdmin = isFinanceAdminUser(user?.email);
  const showMarketingTools = canAccessMarketingTools({
    email: user?.email,
    proId: user?.pro_id,
  });
  const showFollowEngine = canAccessFollowEngine(user?.email);
  const { i18n } = useTranslation();
  const navigate = useNavigate();

  // Détection du mode iOS Reader App (masquer fonctionnalités premium pour utilisateurs gratuits iOS)
  const { isIOSFreeUser } = useIOSReaderMode();
  const { isFeatureEnabled } = useFeatureFlags();

  // Utilisation de la version sécurisée du hook qui donne accès aux conversations et à la fonction refresh
  const { conversations, refreshUnreadMessagesCount } = useSafeTwilioConversations();

  // État local pour stocker le nombre de messages non lus
  const [unreadCount, setUnreadCount] = useState(0);

  // Fonction pour compter manuellement les messages non lus
  const countUnreadMessages = useCallback(() => {
    if (!conversations || !Array.isArray(conversations)) return 0;

    let count = 0;
    for (const conv of conversations) {
      if ((conv.unreadMessagesCount && conv.unreadMessagesCount > 0) || conv.hasUnreadMessages) {
        count++;
        console.log(`Sidebar: Conversation ${conv.conversation_sid} a des messages non lus. ` +
          `unreadMessagesCount=${conv.unreadMessagesCount}, ` +
          `hasUnreadMessages=${conv.hasUnreadMessages}`);
      }
    }

    console.log(`Sidebar: Total messages non lus: ${count}`);
    return count;
  }, [conversations]);

  // Mettre à jour le compteur de messages non lus lorsque les conversations changent
  useEffect(() => {
    // Définir le nombre de messages non lus en se basant sur les conversations
    const newCount = countUnreadMessages();
    setUnreadCount(newCount);

    // Mettre à jour le titre de l'onglet avec le nombre de messages non lus
    updatePageTitle(newCount);

    // Forcer une actualisation des données à intervalle régulier
    const intervalId = setInterval(() => {
      // Cette ligne force une requête au serveur Twilio pour récupérer l'état actuel des messages non lus
      if (refreshUnreadMessagesCount) {
        console.log("Sidebar: Forçage d'actualisation périodique des compteurs");
        refreshUnreadMessagesCount();
      }
    }, 3000); // Actualiser toutes les 3 secondes

    return () => clearInterval(intervalId);
  }, [conversations, countUnreadMessages, refreshUnreadMessagesCount]);

  // Mettre à jour le titre de la page avec le nombre de messages non lus
  const updatePageTitle = useCallback((count: number) => {
    const baseTitle = "Rintintin Backoffice";
    if (count > 0) {
      document.title = `(${count}) ${baseTitle}`;
    } else {
      document.title = baseTitle;
    }
  }, []);

  // Effet spécifique pour la page messages
  useEffect(() => {
    // Si nous sommes sur la page des messages, forcer une actualisation immédiate
    if (location.pathname.includes('/messages') && refreshUnreadMessagesCount) {
      console.log("Sidebar: Détection de la page messages, actualisation forcée");
      refreshUnreadMessagesCount();
    }
  }, [location.pathname, refreshUnreadMessagesCount]);

  // État indiquant si on est sur mobile
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [isBugModalOpen, setIsBugModalOpen] = useState(false);
  // État indiquant si la sidebar est ouverte ou fermée
  const [isSidebarOpen, setIsSidebarOpen] = useState(!isMobile);
  // Un seul groupe déroulant ouvert à la fois (accordéon)
  const [openAccordionSection, setOpenAccordionSection] =
    useState<SidebarAccordionSection | null>(() =>
      resolveAccordionSectionForPath(location.pathname)
    );
  const isEssentialMenuOpen = openAccordionSection === 'essential';
  const isServicesMenuOpen = openAccordionSection === 'offers';
  const isBusinessMenuOpen = openAccordionSection === 'business';
  const isProfileMenuOpen = openAccordionSection === 'profile';
  const isEssentialSectionActive =
    location.pathname.startsWith('/app/planning') ||
    location.pathname.startsWith('/app/services') ||
    location.pathname.startsWith('/app/pension');
  const isOffersSectionActive = Object.values(OFFERS_PRODUCT_PATHS).some((path) =>
    location.pathname.startsWith(path)
  );
  const isBusinessSectionActive = BUSINESS_SECTION_PATHS.some((path) =>
    location.pathname.startsWith(path)
  );

  const openAccordionSectionOnly = useCallback(
    (section: SidebarAccordionSection) => {
      setOpenAccordionSection(section);
    },
    []
  );

  const toggleAccordionSection = useCallback((section: SidebarAccordionSection) => {
    setOpenAccordionSection((prev) => (prev === section ? null : section));
  }, []);

  // Surveiller le redimensionnement de la fenêtre pour mettre à jour isMobile
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      // Si on repasse en desktop, on force la sidebar à être ouverte
      if (!mobile) {
        setIsSidebarOpen(true);
      }
    };

    window.addEventListener('resize', handleResize);
    handleResize(); // Pour initialiser correctement au premier rendu
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const { billingUi, country: proCountry } = useProCountryFeatures();
  const boardingModuleVisible = useBoardingModuleVisible();
  const showClubSubscriptionsNav = canAccessClubSubscriptionsUi();

  // Quand on change de route en mobile, on ferme la sidebar
  useEffect(() => {
    if (isMobile) {
      setIsSidebarOpen(false);
    }
  }, [location.pathname, isMobile]);

  // Ouvrir / fermer la sidebar
  const toggleSidebar = () => {
    setIsSidebarOpen((prev) => !prev);
  };

  // Ouvrir / fermer le menu profil
  const toggleProfileMenu = () => {
    toggleAccordionSection('profile');
  };

  // Clic sur l'en-tête « Offres » : ouvre/ferme le sous-menu, sans naviguer vers le premier enfant.
  const toggleOffersMenu = () => {
    toggleAccordionSection('offers');
  };
  const toggleBusinessMenu = () => {
    toggleAccordionSection('business');
  };
  // Clic sur l'en-tête « Essentiel » : navigue vers le planning et ouvre le sous-menu.
  // Si on est déjà sur planning, services ou pension, simple toggle (ouvrir/fermer).
  const toggleEssentialMenu = () => {
    if (!isEssentialSectionActive) {
      openAccordionSectionOnly('essential');
      navigate('/app/planning');
      if (isMobile) {
        toggleSidebar();
      }
      return;
    }
    toggleAccordionSection('essential');
  };

  // Ouvrir le parent correspondant à la route active (ferme les autres via l'état unique)
  useEffect(() => {
    const sectionForPath = resolveAccordionSectionForPath(location.pathname);
    if (sectionForPath) {
      setOpenAccordionSection(sectionForPath);
    }
  }, [location.pathname]);

  // Écouteurs pour le tour d'onboarding (pour ouvrir les menus automatiquement)
  useEffect(() => {
    const openOffres = () => openAccordionSectionOnly('offers');
    const openEssential = () => openAccordionSectionOnly('essential');
    const openBusiness = () => openAccordionSectionOnly('business');
    const openProfile = () => openAccordionSectionOnly('profile');
    const closeMenus = () => {
      setOpenAccordionSection(null);
    };

    window.addEventListener('onboarding:open-offres', openOffres);
    window.addEventListener('onboarding:open-essential', openEssential);
    window.addEventListener('onboarding:open-business', openBusiness);
    window.addEventListener('onboarding:open-profile', openProfile);
    window.addEventListener('onboarding:close-menus', closeMenus);

    return () => {
      window.removeEventListener('onboarding:open-offres', openOffres);
      window.removeEventListener('onboarding:open-essential', openEssential);
      window.removeEventListener('onboarding:open-business', openBusiness);
      window.removeEventListener('onboarding:open-profile', openProfile);
      window.removeEventListener('onboarding:close-menus', closeMenus);
    };
  }, [openAccordionSectionOnly]);

  // URL de la photo de profil par défaut si celle de l'utilisateur n'est pas disponible
  const defaultProfilePic = "https://rintintin-bucket.s3.eu-west-3.amazonaws.com/default-profile.jpg";
  const profilePic = user?.profile_photo_url || defaultProfilePic;
  const proName = user?.name || "Pro";

  // --- Onboarding progress (pour badge rouge sur Profil) ---
  const userScopedKey = (userId?: number | string, suffix?: string) => `onboarding:v1:user:${userId ?? 'anon'}:${suffix ?? ''}`;

  /** Même source que userData / login : évite la clé `anon` tant que le contexte React n'a pas encore le pro_id. */
  const resolveProIdForOnboarding = useCallback((): number | string | 'anon' => {
    if (user?.pro_id != null) {
      return user.pro_id;
    }
    try {
      const saved = localStorage.getItem('userData');
      if (saved) {
        const u = JSON.parse(saved) as { pro_id?: number | string };
        if (u?.pro_id != null && u.pro_id !== '') return u.pro_id;
      }
    } catch {
      /* ignore */
    }
    const raw = localStorage.getItem('pro_id');
    if (raw) {
      const n = parseInt(raw, 10);
      if (!Number.isNaN(n)) return n;
    }
    return 'anon';
  }, [user?.pro_id]);

  /** Une fois `user` chargé, ne pas se fier au JSON localStorage (souvent désynchronisé). */
  const isOnboardingCompletedAnchored = useCallback((): boolean => {
    if (user != null) {
      return isValidOnboardingTimestamp(user.onboarding_completed_at);
    }
    try {
      const saved = localStorage.getItem('userData');
      if (saved) {
        const u = JSON.parse(saved) as { onboarding_completed_at?: string | null };
        return isValidOnboardingTimestamp(u?.onboarding_completed_at);
      }
    } catch {
      /* ignore */
    }
    return false;
  }, [user]);
  const isStepDone = useCallback((key: 'profile' | 'service' | 'availability' | 'calendar') => {
    try {
      if (key === 'profile') {
        return Boolean(user?.profile_complete || user?.profile_photo_url) ||
          localStorage.getItem(userScopedKey(user?.pro_id, `done:${key}`)) === '1' ||
          localStorage.getItem(`onboarding:done:${key}`) === '1';
      }
      if (key === 'service') {
        const scoped = Number(localStorage.getItem(userScopedKey(user?.pro_id, 'lastKnownServicesCount')) || '0');
        const legacy = Number(localStorage.getItem('lastKnownServicesCount') || '0');
        const has = Math.max(scoped, legacy) > 0;
        return has || localStorage.getItem(userScopedKey(user?.pro_id, `done:${key}`)) === '1' || localStorage.getItem(`onboarding:done:${key}`) === '1';
      }
      if (key === 'availability') {
        if (isValidOnboardingTimestamp(user?.onboarding_completed_at)) return true; // Validé en base = TERMINÉ
        const scoped = Number(localStorage.getItem(userScopedKey(user?.pro_id, 'lastKnownAvailabilityCount')) || '0');
        const has = scoped > 0;
        return has || localStorage.getItem(userScopedKey(user?.pro_id, `done:${key}`)) === '1';
      }
      if (key === 'calendar') {
        const scoped = localStorage.getItem(userScopedKey(user?.pro_id, 'googleCalendarConnected')) === '1';
        const legacy = localStorage.getItem('googleCalendarConnected') === '1';
        const has = scoped || legacy;
        return has || localStorage.getItem(userScopedKey(user?.pro_id, `done:${key}`)) === '1' || localStorage.getItem(`onboarding:done:${key}`) === '1';
      }
      return false;
    } catch (e) {
      return false;
    }
  }, [user?.pro_id, user?.profile_complete, user?.profile_photo_url, user?.onboarding_completed_at]);

  const [onboardingComplete, setOnboardingComplete] = useState<boolean>(true);

  // Lecture synchrone depuis userData localStorage pour éviter tout flash visuel.
  // Priorité : (1) onboarding_completed_at dans userData, (2) startup_dismissed en localStorage.
  const [startupDismissed, setStartupDismissed] = useState<boolean>(() => {
    try {
      const saved = localStorage.getItem('userData');
      if (saved) {
        const u = JSON.parse(saved);
        if (isValidOnboardingTimestamp(u.onboarding_completed_at)) return true;
        const proId = u.pro_id ?? localStorage.getItem('pro_id');
        if (proId) {
          const key = `onboarding:v1:user:${proId}:startup_dismissed`;
          if (localStorage.getItem(key) === '1') return true;
          // Pro connu, pas terminé en base, pas dismiss : afficher tout de suite l'onglet
          return false;
        }
      }
    } catch { /* */ }
    return true; // Pas de contexte pro fiable : caché jusqu'au recalcul (effect)
  });
  useEffect(() => {
    const compute = () => {
      const complete = (
        isStepDone('profile') &&
        isStepDone('service') &&
        isStepDone('calendar')
      );
      setOnboardingComplete(complete);

      try {
        const pid = resolveProIdForOnboarding();
        const dismissedKey = `onboarding:v1:user:${pid}:startup_dismissed`;
        setStartupDismissed(
          localStorage.getItem(dismissedKey) === '1' || isOnboardingCompletedAnchored()
        );
      } catch {
        setStartupDismissed(false);
      }
    };
    compute();
    const handle = () => compute();
    window.addEventListener('storage', handle);
    const interval = setInterval(compute, 1500);
    return () => {
      window.removeEventListener('storage', handle);
      clearInterval(interval);
    };
  }, [isStepDone, user?.pro_id, user?.onboarding_completed_at, resolveProIdForOnboarding, isOnboardingCompletedAnchored]);

  const showStartupTab = !startupDismissed;
  const { corePercent } = useOnboardingCoreProgress(showStartupTab);

  // Fonction pour naviguer vers la page d'abonnement
  const goToPremium = () => {
    navigateToSubscription(navigate, 'sidebar');
    if (isMobile) toggleSidebar();
  };

  // Gérer le clic sur le lien Messages - force un rafraîchissement des compteurs
  const handleMessagesClick = () => {
    if (refreshUnreadMessagesCount) {
      console.log("Sidebar: Clic sur Messages, actualisation forcée");
      refreshUnreadMessagesCount();
    }

    if (isMobile) toggleSidebar();
  };

  const openBugReportFromFooter = useCallback(() => {
    setIsBugModalOpen(true);
    if (isMobile) toggleSidebar();
  }, [isMobile]);

  // Détection du contexte association
  const { isInAssociation, associationSlug } = useAssociationContext();

  // État pour les infos de l'association
  const [associationInfo, setAssociationInfo] = useState<{
    name: string;
    logo_url?: string;
    circular_logo_url?: string;
  } | null>(null);
  const [loadingAssociation, setLoadingAssociation] = useState(false);

  // Fetch les infos de l'association quand on est en mode association
  useEffect(() => {
    const fetchAssociationInfo = async () => {
      if (!isInAssociation || !associationSlug) {
        setAssociationInfo(null);
        return;
      }

      const token = localStorage.getItem('token');
      if (!token) {
        console.warn('Sidebar: Pas de token disponible pour fetch association');
        return;
      }

      try {
        setLoadingAssociation(true);
        console.log('Sidebar: Fetch association info pour slug:', associationSlug);
        const response = await axios.get(
          configService.getFullApiUrl(`/associations/${associationSlug}`),
          { headers: { Authorization: `Bearer ${token}` } }
        );
        console.log('Sidebar: Association info reçue:', response.data);
        setAssociationInfo({
          name: response.data.association?.name || 'Association',
          logo_url: response.data.association?.logo_url,
          circular_logo_url: response.data.association?.circular_logo_url
        });
      } catch (error: any) {
        console.error('Sidebar: Erreur fetch association info:', error);
        console.error('Sidebar: Détails erreur:', {
          status: error.response?.status,
          message: error.response?.data?.error || error.message,
          url: error.config?.url
        });
        // Afficher quand même le menu avec un nom par défaut
        setAssociationInfo({ name: 'Association' });
      } finally {
        setLoadingAssociation(false);
      }
    };

    fetchAssociationInfo();
  }, [isInAssociation, associationSlug]);

  // Fonction pour retourner au compte pro
  const handleBackToPro = () => {
    navigate('/app');
    if (isMobile) toggleSidebar();
  };

  return (
    <>
      {/* Bouton burger (affiché uniquement en mobile standalone, pas dans le Drawer) */}
      {isMobile && variant !== 'drawer' && (
        <div className="bubble-hamburger-max" onClick={toggleSidebar}>
          <button
            className="hamburger-button"
            aria-label="Ouvrir ou fermer la sidebar"
          >
            <Menu />
          </button>
        </div>
      )}

      {/* Overlay sombre (affiché uniquement si la sidebar est ouverte en mobile) */}
      {isMobile && variant !== 'drawer' && isSidebarOpen && (
        <div className="sidebar-overlay" onClick={toggleSidebar} />
      )}

      {/* La sidebar */}
      <nav
        className={`sidebar ${isSidebarOpen ? 'open' : 'closed'} ${desktopCollapsed && !isMobile ? 'desktop-collapsed' : ''} ${isInAssociation ? 'association-mode' : ''} ${variant === 'drawer' ? 'sidebar--drawer' : ''}`}
        aria-label="Menu de navigation"
        aria-hidden={desktopCollapsed && !isMobile ? true : undefined}
      >
        {/* Header de la sidebar */}
        {isInAssociation ? (
          <div className="sidebar-title association-header">
            {loadingAssociation ? (
              <div className="association-header-content">
                <span className="association-name">{t('sidebar:loading')}</span>
              </div>
            ) : associationInfo ? (
              <a
                href={`https://rintintin-booking.com/association/${associationSlug}`}
                target="_blank"
                rel="noopener noreferrer"
                className="association-header-link"
                onClick={(e) => {
                  // Ne pas fermer la sidebar sur mobile lors du clic
                  e.stopPropagation();
                }}
              >
                <div className="association-header-content">
                  {(associationInfo.circular_logo_url || associationInfo.logo_url) ? (
                    <img
                      src={associationInfo.circular_logo_url || associationInfo.logo_url}
                      alt={associationInfo.name}
                      className="association-logo"
                    />
                  ) : (
                    <GroupAdd className="association-logo-icon" />
                  )}
                  <span className="association-name">{associationInfo.name}</span>
                </div>
              </a>
            ) : (
              <div className="association-header-content">
                <GroupAdd className="association-logo-icon" />
                <span className="association-name">Association</span>
              </div>
            )}
          </div>
        ) : (
          <>
            <div className="sidebar-title">
              <button
                type="button"
                className="sidebar-title-logo-btn"
                onClick={() => navigate('/')}
                aria-label={t('sidebar:menu.home')}
              >
                <Logo fontSize="1.6rem" />
              </button>
              {user && <ProSidebarPlanStatus isPremium={user.is_premium} />}
            </div>
            {user && (
              <ProSidebarPlanCta
                isPremium={user.is_premium}
                onUpgrade={goToPremium}
              />
            )}
          </>
        )}

        {/* Bouton retour au compte pro (en mode association) */}
        {isInAssociation && (
          <div className="association-back-section">
            <button
              className="association-back-button"
              onClick={handleBackToPro}
            >
              <ArrowBack style={{ marginRight: '8px' }} />
              {t('sidebar:backToPro')}
            </button>
          </div>
        )}

        {/* Menu conditionnel : Pro ou Association */}
        {isInAssociation && associationSlug ? (
          /* Menu Association */
          <>
            <ul className="sidebar-list">
              <li>
                <NavLink
                  to={`/app/association/${associationSlug}/planning`}
                  className={({ isActive }) =>
                    isActive ? 'sidebar-link active' : 'sidebar-link'
                  }
                  onClick={isMobile ? toggleSidebar : undefined}
                >
                  <CalendarMonth style={{ marginRight: '8px' }} />
                  {t('sidebar:menu.planning')}
                </NavLink>
              </li>
              <li>
                <NavLink
                  to={`/app/association/${associationSlug}/members`}
                  className={({ isActive }) =>
                    isActive ? 'sidebar-link active' : 'sidebar-link'
                  }
                  onClick={isMobile ? toggleSidebar : undefined}
                >
                  <Groups style={{ marginRight: '8px' }} />
                  {t('sidebar:menu.members')}
                </NavLink>
              </li>
              <li>
                <NavLink
                  to={`/app/association/${associationSlug}/clients`}
                  className={({ isActive }) =>
                    isActive ? 'sidebar-link active' : 'sidebar-link'
                  }
                  onClick={isMobile ? toggleSidebar : undefined}
                >
                  <PersonOutline style={{ marginRight: '8px' }} />
                  {t('sidebar:menu.clients')}
                </NavLink>
              </li>
              <li>
                <NavLink
                  to={`/app/association/${associationSlug}/events`}
                  className={({ isActive }) =>
                    isActive ? 'sidebar-link active' : 'sidebar-link'
                  }
                  onClick={isMobile ? toggleSidebar : undefined}
                >
                  <CalendarMonth style={{ marginRight: '8px' }} />
                  {t('sidebar:menu.events')}
                </NavLink>
              </li>
              {isFeatureEnabled(FeatureFlag.CHAT_FEATURE) && (
              <li>
                <NavLink
                  to={`/app/association/${associationSlug}/messaging`}
                  className={({ isActive }) =>
                    isActive ? 'sidebar-link active' : 'sidebar-link'
                  }
                  onClick={isMobile ? toggleSidebar : undefined}
                >
                  <SmsOutlined style={{ marginRight: '8px' }} />
                  {t('sidebar:menu.messaging')}
                </NavLink>
              </li>
              )}
              <li>
                <NavLink
                  to={`/app/association/${associationSlug}/cgv`}
                  className={({ isActive }) =>
                    isActive ? 'sidebar-link active' : 'sidebar-link'
                  }
                  onClick={isMobile ? toggleSidebar : undefined}
                >
                  <DescriptionOutlined style={{ marginRight: '8px' }} />
                  {t('sidebar:menu.cgv')}
                </NavLink>
              </li>
              <li>
                <NavLink
                  to={`/app/association/${associationSlug}/settings`}
                  className={({ isActive }) =>
                    isActive ? 'sidebar-link active' : 'sidebar-link'
                  }
                  onClick={isMobile ? toggleSidebar : undefined}
                >
                  <Settings style={{ marginRight: '8px' }} />
                  {t('sidebar:menu.settings')}
                </NavLink>
              </li>
            </ul>
          </>
        ) : (
          /* Menu Pro (défaut) */
          <>
            {/* Opérations */}
            {showStartupTab && (
              <div className="sidebar-startup-banner">
                <NavLink
                  to="/app/demarrage"
                  className={({ isActive }) =>
                    isActive ? 'sidebar-link active startup-link' : 'sidebar-link startup-link'
                  }
                  onClick={isMobile ? toggleSidebar : undefined}
                  {...buildPreloadHandlers('/app/demarrage', isMobile)}
                >
                  <span className="startup-link__row">
                    <RocketLaunch style={{ marginRight: '8px' }} />
                    <span>Démarrage</span>
                    {!onboardingComplete && (
                      corePercent > 0
                        ? <span className="startup-progress-pct">{corePercent}%</span>
                        : <span className="startup-dot" />
                    )}
                  </span>
                  {!onboardingComplete && corePercent > 0 && (
                    <span className="startup-progress-track">
                      <span
                        className="startup-progress-fill"
                        style={{ width: `${corePercent}%` }}
                      />
                    </span>
                  )}
                </NavLink>
              </div>
            )}
            <div className="sidebar-nav-body">
            <ul className="sidebar-list">
              <li>
                <NavLink
                  to="/app"
                  end
                  className={({ isActive }) =>
                    isActive ? 'sidebar-link active' : 'sidebar-link'
                  }
                  onClick={isMobile ? toggleSidebar : undefined}
                  {...buildPreloadHandlers('/app', isMobile)}
                >
                  <HomeOutlined style={{ marginRight: '8px' }} />
                  {t('sidebar:menu.home')}
                </NavLink>
              </li>
              {isMobile && boardingModuleVisible && (
                <li>
                  <NavLink
                    to="/app/pension"
                    id="nav-pension-mobile-top"
                    className={({ isActive }) =>
                      isActive ? 'sidebar-link active' : 'sidebar-link'
                    }
                    onClick={toggleSidebar}
                    {...buildPreloadHandlers('/app/pension', isMobile)}
                  >
                    <Apartment style={{ marginRight: '8px' }} />
                    {t('sidebar:menu.pension')}
                  </NavLink>
                </li>
              )}
              <li>
                <div
                  className={`dropdown-header${isEssentialSectionActive ? ' active' : ''}`}
                  id="nav-planning"
                  onClick={toggleEssentialMenu}
                >
                  <div className="dropdown-title">
                    <CalendarMonth style={{ marginRight: '8px' }} />
                    {t('sidebar:menu.essential')}
                  </div>
                  {isEssentialMenuOpen ?
                    <ArrowDropUp className="dropdown-caret" /> :
                    <ArrowDropDown className="dropdown-caret" />
                  }
                </div>

                <div className={`sidebar-dropdown ${isEssentialMenuOpen ? 'open' : ''}`}>
                  <NavLink
                    to="/app/planning"
                    id="nav-planning-calendar"
                    className={({ isActive }) =>
                      isActive ? 'dropdown-link active' : 'dropdown-link'
                    }
                    onClick={isMobile ? toggleSidebar : undefined}
                    {...buildPreloadHandlers('/app/planning', isMobile)}
                  >
                    <CalendarMonth style={{ marginRight: '8px' }} />
                    {t('sidebar:menu.planning')}
                  </NavLink>

                  {boardingModuleVisible && (
                    <NavLink
                      to="/app/pension"
                      id="nav-planning-pension"
                      className={({ isActive }) =>
                        isActive ? 'dropdown-link active' : 'dropdown-link'
                      }
                      onClick={isMobile ? toggleSidebar : undefined}
                      {...buildPreloadHandlers('/app/pension', isMobile)}
                    >
                      <Apartment style={{ marginRight: '8px' }} />
                      {t('sidebar:menu.pension')}
                    </NavLink>
                  )}

                  <NavLink
                    to="/app/services"
                    id="nav-services"
                    className={({ isActive }) =>
                      isActive ? 'dropdown-link active' : 'dropdown-link'
                    }
                    onClick={isMobile ? toggleSidebar : undefined}
                    {...buildPreloadHandlers('/app/services', isMobile)}
                  >
                    <Apps style={{ marginRight: '8px' }} />
                    {t('sidebar:menu.services')}
                  </NavLink>
                </div>
              </li>
              <li>
                <NavLink
                  to="/app/reservations"
                  id="nav-reservations"
                  className={({ isActive }) =>
                    isActive ? 'sidebar-link active' : 'sidebar-link'
                  }
                  onClick={isMobile ? toggleSidebar : undefined}
                  {...buildPreloadHandlers('/app/reservations', isMobile)}
                >
                  <MenuBook style={{ marginRight: '8px' }} />
                  {t('sidebar:menu.reservations')}
                </NavLink>
              </li>

              {!isIOSFreeUser && (
                <li>
                  <div
                    className={`dropdown-header${isOffersSectionActive ? ' active' : ''}${showPremiumMarks && isPremiumSidebarNav('forfaits') ? ' is-premium-locked' : ''}`}
                    id="nav-offres-header"
                    onClick={toggleOffersMenu}
                  >
                    <div className="dropdown-title">
                      <Storefront style={{ marginRight: '8px' }} />
                      <span className="sidebar-nav-label">{t('sidebar:menu.offers')}</span>
                      {showPremiumMarks && isPremiumSidebarNav('forfaits') && (
                        <SidebarPremiumMark label={premiumMarkLabel} />
                      )}
                    </div>
                    {isServicesMenuOpen ?
                      <ArrowDropUp className="dropdown-caret" /> :
                      <ArrowDropDown className="dropdown-caret" />
                    }
                  </div>

                  <div className={`sidebar-dropdown ${isServicesMenuOpen ? 'open' : ''}`}>
                    <NavLink
                      to={OFFERS_PRODUCT_PATHS.forfaits}
                      id="nav-forfaits"
                      className={({ isActive }) =>
                        `dropdown-link${isActive ? ' active' : ''}${showPremiumMarks && isPremiumSidebarNav('forfaits') ? ' is-premium-locked' : ''}`
                      }
                      onClick={isMobile ? toggleSidebar : undefined}
                      {...buildPreloadHandlers(OFFERS_PRODUCT_PATHS.forfaits, isMobile)}
                    >
                      <LocalOffer style={{ marginRight: '8px' }} />
                      <span className="sidebar-nav-label">{t('sidebar:menu.packages')}</span>
                      {showPremiumMarks && isPremiumSidebarNav('forfaits') && (
                        <SidebarPremiumMark label={premiumMarkLabel} />
                      )}
                    </NavLink>
                    <NavLink
                      to={OFFERS_PRODUCT_PATHS.credits}
                      id="nav-credits"
                      className={({ isActive }) =>
                        `dropdown-link${isActive ? ' active' : ''}${showPremiumMarks && isPremiumSidebarNav('forfaits') ? ' is-premium-locked' : ''}`
                      }
                      onClick={isMobile ? toggleSidebar : undefined}
                      {...buildPreloadHandlers(OFFERS_PRODUCT_PATHS.credits, isMobile)}
                    >
                      <AccountBalanceWalletOutlined style={{ marginRight: '8px' }} />
                      <span className="sidebar-nav-label">{t('sidebar:menu.credits')}</span>
                      {showPremiumMarks && isPremiumSidebarNav('forfaits') && (
                        <SidebarPremiumMark label={premiumMarkLabel} />
                      )}
                    </NavLink>
                    <NavLink
                      to={OFFERS_PRODUCT_PATHS.gifts}
                      id="nav-gift-vouchers"
                      className={({ isActive }) =>
                        `dropdown-link${isActive ? ' active' : ''}${showPremiumMarks && isPremiumSidebarNav('forfaits') ? ' is-premium-locked' : ''}`
                      }
                      onClick={isMobile ? toggleSidebar : undefined}
                      {...buildPreloadHandlers(OFFERS_PRODUCT_PATHS.gifts, isMobile)}
                    >
                      <CardGiftcard style={{ marginRight: '8px' }} />
                      <span className="sidebar-nav-label">{t('sidebar:menu.giftVouchers')}</span>
                      {showPremiumMarks && isPremiumSidebarNav('forfaits') && (
                        <SidebarPremiumMark label={premiumMarkLabel} />
                      )}
                    </NavLink>
                    {showClubSubscriptionsNav && (
                      <NavLink
                        to={OFFERS_PRODUCT_PATHS.club}
                        id="nav-club-subscriptions"
                        className={({ isActive }) =>
                          `dropdown-link${isActive ? ' active' : ''}${showPremiumMarks && isPremiumSidebarNav('forfaits') ? ' is-premium-locked' : ''}`
                        }
                        onClick={isMobile ? toggleSidebar : undefined}
                        {...buildPreloadHandlers(OFFERS_PRODUCT_PATHS.club, isMobile)}
                      >
                        <AutorenewOutlined style={{ marginRight: '8px' }} />
                        <span className="sidebar-nav-label">{t('sidebar:menu.subscriptions')}</span>
                        {showPremiumMarks && isPremiumSidebarNav('forfaits') && (
                          <SidebarPremiumMark label={premiumMarkLabel} />
                        )}
                      </NavLink>
                    )}
                  </div>
                </li>
              )}
            </ul>

            {/* Business (menu déroulant) - Masqué sur iOS free */}
            {!isIOSFreeUser && (
              <ul className="sidebar-list">
                <li>
                  <div
                    className={`dropdown-header${isBusinessSectionActive ? ' active' : ''}${showPremiumMarks ? ' is-premium-locked' : ''}`}
                    id="nav-business-header"
                    onClick={toggleBusinessMenu}
                  >
                    <div className="dropdown-title">
                      <AttachMoney style={{ marginRight: '8px' }} />
                      <span className="sidebar-nav-label">{t('sidebar:menu.business')}</span>
                      {showPremiumMarks && (
                        <SidebarPremiumMark label={premiumMarkLabel} />
                      )}
                    </div>
                    {isBusinessMenuOpen ? (
                      <ArrowDropUp className="dropdown-caret" />
                    ) : (
                      <ArrowDropDown className="dropdown-caret" />
                    )}
                  </div>
                  <div className={`sidebar-dropdown ${isBusinessMenuOpen ? 'open' : ''}`}>
                    {billingUi && (
                      <NavLink
                        to="/app/billing"
                        id="nav-billing"
                        className={({ isActive }) =>
                          `dropdown-link${isActive ? ' active' : ''}${showPremiumMarks && isPremiumSidebarNav('billing') ? ' is-premium-locked' : ''}`
                        }
                        onClick={isMobile ? toggleSidebar : undefined}
                        {...buildPreloadHandlers('/app/billing', isMobile)}
                      >
                        <CreditCard style={{ marginRight: '8px' }} />
                        <span className="sidebar-nav-label">{t('sidebar:menu.billing')}</span>
                        {showPremiumMarks && isPremiumSidebarNav('billing') && (
                          <SidebarPremiumMark label={premiumMarkLabel} />
                        )}
                      </NavLink>
                    )}
                    {billingUi && (
                      <NavLink
                        to="/app/quotes"
                        id="nav-quotes"
                        className={({ isActive }) =>
                          `dropdown-link${isActive ? ' active' : ''}${showPremiumMarks && isPremiumSidebarNav('quotes') ? ' is-premium-locked' : ''}`
                        }
                        onClick={isMobile ? toggleSidebar : undefined}
                        {...buildPreloadHandlers('/app/quotes', isMobile)}
                      >
                        <DescriptionOutlined style={{ marginRight: '8px' }} />
                        <span className="sidebar-nav-label">{t('sidebar:menu.quotes')}</span>
                        {showPremiumMarks && isPremiumSidebarNav('quotes') && (
                          <SidebarPremiumMark label={premiumMarkLabel} />
                        )}
                      </NavLink>
                    )}
                    <NavLink
                      to="/app/comptabilite"
                      className={({ isActive }) =>
                        `dropdown-link${isActive ? ' active' : ''}${showPremiumMarks && isPremiumSidebarNav('comptabilite') ? ' is-premium-locked' : ''}`
                      }
                      onClick={isMobile ? toggleSidebar : undefined}
                      {...buildPreloadHandlers('/app/comptabilite', isMobile)}
                    >
                      <AttachMoney style={{ marginRight: '8px' }} />
                      <span className="sidebar-nav-label">{t('sidebar:menu.expenses')}</span>
                      {showPremiumMarks && isPremiumSidebarNav('comptabilite') && (
                        <SidebarPremiumMark label={premiumMarkLabel} />
                      )}
                    </NavLink>
                    <NavLink
                      to="/app/stats"
                      className={({ isActive }) =>
                        `dropdown-link${isActive ? ' active' : ''}${showPremiumMarks && isPremiumSidebarNav('stats') ? ' is-premium-locked' : ''}`
                      }
                      onClick={isMobile ? toggleSidebar : undefined}
                      {...buildPreloadHandlers('/app/stats', isMobile)}
                    >
                      <ShowChart style={{ marginRight: '8px' }} />
                      <span className="sidebar-nav-label">{t('sidebar:menu.statistics')}</span>
                      {showPremiumMarks && isPremiumSidebarNav('stats') && (
                        <SidebarPremiumMark label={premiumMarkLabel} />
                      )}
                    </NavLink>
                    {showFinanceAdmin && (
                      <NavLink
                        to="/app/finance"
                        id="nav-finance-admin"
                        className={({ isActive }) => (isActive ? 'dropdown-link active' : 'dropdown-link')}
                        onClick={isMobile ? toggleSidebar : undefined}
                        {...buildPreloadHandlers('/app/finance', isMobile)}
                      >
                        <AccountBalanceOutlined style={{ marginRight: '8px' }} />
                        {t('sidebar:menu.finance')}
                      </NavLink>
                    )}
                  </div>
                </li>
              </ul>
            )}

            {showMarketingTools && !isIOSFreeUser && (
              <ul className="sidebar-list">
                <li>
                  <NavLink
                    to="/app/marketing"
                    id="nav-marketing-tools"
                    className={({ isActive }) =>
                      `sidebar-link${isActive ? ' active' : ''}${showPremiumMarks && isPremiumSidebarNav('marketing') ? ' is-premium-locked' : ''}`
                    }
                    onClick={isMobile ? toggleSidebar : undefined}
                    {...buildPreloadHandlers('/app/marketing', isMobile)}
                  >
                    <Share style={{ marginRight: '8px' }} />
                    <span className="sidebar-nav-label">{t('sidebar:menu.marketing')}</span>
                    {showPremiumMarks && isPremiumSidebarNav('marketing') && (
                      <SidebarPremiumMark label={premiumMarkLabel} />
                    )}
                  </NavLink>
                </li>
              </ul>
            )}

            {/* Clients & Chiens */}
            <ul className="sidebar-list">
              <li>
                <NavLink
                  to="/app/clients"
                  className={({ isActive }) =>
                    isActive ? 'sidebar-link active' : 'sidebar-link'
                  }
                  onClick={isMobile ? toggleSidebar : undefined}
                  {...buildPreloadHandlers('/app/clients', isMobile)}
                >
                  <Groups style={{ marginRight: '8px' }} />
                  {t('sidebar:menu.clientsAndDogs')}
                </NavLink>
              </li>
            </ul>

            {showFollowEngine && (
              <ul className="sidebar-list">
                <li>
                  <NavLink
                    to="/app/suivis"
                    id="nav-follow-engine"
                    className={({ isActive }) =>
                      isActive ? 'sidebar-link active' : 'sidebar-link'
                    }
                    onClick={isMobile ? toggleSidebar : undefined}
                    {...buildPreloadHandlers('/app/suivis', isMobile)}
                  >
                    <Pets style={{ marginRight: '8px' }} />
                    {t('sidebar:menu.follow')}
                  </NavLink>
                </li>
              </ul>
            )}

            {/* Mes SMS (Masqué) */}
            {/*
            {!isIOSFreeUser && (
              <ul className="sidebar-list">
                <li>
                  <NavLink
                    to="/app/marketing?tab=sms"
                    className={({ isActive }) =>
                      isActive ? 'sidebar-link active' : 'sidebar-link'
                    }
                    onClick={isMobile ? toggleSidebar : undefined}
                  >
                    <Send style={{ marginRight: '8px' }} />
                    Mes SMS
                  </NavLink>
                </li>
              </ul>
            )}
            */}

            {/* Notes - Masqué sur iOS free */}
            {!isIOSFreeUser && (
              <ul className="sidebar-list">
                <li>
                  <NavLink
                    to="/app/notes"
                    className={({ isActive }) =>
                      `sidebar-link${isActive ? ' active' : ''}${showPremiumMarks && isPremiumSidebarNav('notes') ? ' is-premium-locked' : ''}`
                    }
                    onClick={isMobile ? toggleSidebar : undefined}
                    {...buildPreloadHandlers('/app/notes', isMobile)}
                  >
                    <DescriptionOutlined style={{ marginRight: '8px' }} />
                    <span className="sidebar-nav-label">{t('sidebar:menu.notes')}</span>
                    {showPremiumMarks && isPremiumSidebarNav('notes') && (
                      <SidebarPremiumMark label={premiumMarkLabel} />
                    )}
                  </NavLink>
                </li>
              </ul>
            )}

            {/* Messages (Supprimé car déplacé dans le header ou non désiré) */}
            {/* 
            {isMobile && !isIOSFreeUser && (
              <ul className="sidebar-list">
                <li>
                  <NavLink
                    to="/app/messages"
                    id="nav-messages"
                    className={({ isActive }) =>
                      isActive ? 'sidebar-link active' : 'sidebar-link'
                    }
                    onClick={handleMessagesClick}
                  >
                    <div className="badge">
                      <SmsOutlined style={{ marginRight: '8px' }} />
                      {unreadCount > 0 && (
                        <span className="unread-badge">{unreadCount}</span>
                      )}
                    </div>
                    {t('sidebar:menu.messages')}
                  </NavLink>
                </li>
              </ul>
            )}
            */}

            </div>

          </>
        )}

        {/* Profil du pro avec menu déroulant (Affiché uniquement sur mobile now) */}
        {isMobile && (
          <div className="profile-section">
            <div className="profile-header" onClick={toggleProfileMenu}>
              <div className="profile-info">
                <div className="profile-pic-wrap">
                  <img src={profilePic} alt="Profil" className="profile-pic" />
                  {!onboardingComplete && isProfileMenuOpen && (
                    <span className="profile-pic-badge" />
                  )}
                </div>
                <span className="pro-name">{proName}</span>
              </div>
              {isProfileMenuOpen ?
                <ArrowDropUp className="profile-caret" /> :
                <ArrowDropDown className="profile-caret" />
              }
            </div>

            <div className={`profile-dropdown ${isProfileMenuOpen ? 'open' : ''}`}>
              <NavLink
                to="/app/profil-v2"
                className={({ isActive }) =>
                  isActive ? 'dropdown-link active' : 'dropdown-link'
                }
                onClick={isMobile ? toggleSidebar : undefined}
              >
                <PersonOutline style={{ marginRight: '8px' }} />
                {t('sidebar:menu.profile')}
                {!onboardingComplete && <span className="profile-alert-dot" />}
              </NavLink>
              {/* Association - Masqué sur iOS free */}
              {!isIOSFreeUser && (
                <NavLink
                  to="/app/associations"
                  className={({ isActive }) =>
                    isActive ? 'dropdown-link active' : 'dropdown-link'
                  }
                  onClick={isMobile ? toggleSidebar : undefined}
                >
                  <Groups style={{ marginRight: '8px' }} />
                  {t('sidebar:menu.association')}
                </NavLink>
              )}
              {/* Équipe - Masqué sur iOS free */}
              {!isIOSFreeUser && (
                <NavLink
                  to="/app/equipe"
                  className={({ isActive }) =>
                    isActive ? 'dropdown-link active' : 'dropdown-link'
                  }
                  onClick={isMobile ? toggleSidebar : undefined}
                >
                  <Groups style={{ marginRight: '8px' }} />
                  {t('sidebar:menu.team')}
                </NavLink>
              )}
              <NavLink
                to="/app/settings"
                className={({ isActive }) =>
                  isActive ? 'dropdown-link active' : 'dropdown-link'
                }
                onClick={isMobile ? toggleSidebar : undefined}
              >
                <Settings style={{ marginRight: '8px' }} />
                {t('sidebar:menu.settings')}
              </NavLink>
              {proCountry === 'FR' && (
                <NavLink
                  to="/app/acaced"
                  className={({ isActive }) =>
                    isActive ? 'dropdown-link active' : 'dropdown-link'
                  }
                  onClick={isMobile ? toggleSidebar : undefined}
                  {...buildPreloadHandlers('/app/acaced', isMobile)}
                >
                  <WorkspacePremium style={{ marginRight: '8px' }} />
                  {t('sidebar:menu.acaced')}
                </NavLink>
              )}
              {isFeatureEnabled(FeatureFlag.AMBASSADOR) && (
                <NavLink
                  to="/app/ambassadeur"
                  className={({ isActive }) =>
                    isActive ? 'dropdown-link active' : 'dropdown-link'
                  }
                  onClick={isMobile ? toggleSidebar : undefined}
                  {...buildPreloadHandlers('/app/ambassadeur', isMobile)}
                >
                  <Campaign style={{ marginRight: '8px' }} />
                  {t('sidebar:menu.ambassador')}
                </NavLink>
              )}

              <a
                href={HELP_CENTER_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="dropdown-link help-link-small"
                onClick={isMobile ? toggleSidebar : undefined}
                style={{
                  fontSize: '11px',
                  padding: '4px 16px',
                  opacity: 0.7,
                  marginTop: '4px'
                }}
              >
                <HelpOutline style={{ marginRight: '6px', fontSize: '12px' }} />
                {t('sidebar:menu.helpCenter')}
              </a>

              <button
                type="button"
                className="dropdown-link help-link-small"
                onClick={() => {
                  setIsBugModalOpen(true);
                  if (isMobile) toggleSidebar();
                }}
                style={{
                  fontSize: '11px',
                  padding: '4px 16px',
                  opacity: 0.7,
                  marginTop: '4px'
                }}
              >
                <BugReportOutlined style={{ marginRight: '6px', fontSize: '12px' }} />
                {t('sidebar:menu.reportBug')}
              </button>

              {/* Bouton Premium animé pour les utilisateurs non premium */}
              {/* Masqué sur iOS (Reader App - pas de promotion d'achat) */}
              {user && !user.is_premium && !(Capacitor.getPlatform() === 'ios' && Capacitor.isNativePlatform()) && (
                <div className="premium-button-container">
                  <button className="premium-button" onClick={goToPremium}>
                    <WorkspacePremium className="premium-icon" />
                    <span>{t('sidebar:menu.becomePremium')}</span>
                  </button>
                  <div className="premium-button-shine"></div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Pied : centre d'aide (desktop + drawer mobile) */}
        <div
          className={`sidebar-footer-desktop${isMobile && isProfileMenuOpen ? ' sidebar-footer-desktop--mobile-hidden' : ''}`}
        >
          <div className="sidebar-footer-nav-row">
            {!isMobile && onToggleDesktopCollapse && (
              <button
                type="button"
                className="sidebar-footer-icon-btn sidebar-footer-collapse-btn sidebar-footer-tip-anchor-start"
                data-rtt-sidebar-collapse="true"
                data-sidebar-tip={t('sidebar:toggle.collapse')}
                onClick={onToggleDesktopCollapse}
                aria-label={t('sidebar:toggle.collapse')}
                title={t('sidebar:toggle.collapse')}
              >
                <MenuOpen />
              </button>
            )}
            <div className="sidebar-footer-actions">
              <a
                href={HELP_CENTER_URL}
                target="_blank"
                rel="noopener noreferrer"
                id="nav-help"
                onClick={isMobile ? toggleSidebar : undefined}
                className="sidebar-help-link-desktop sidebar-footer-icon-btn"
                data-sidebar-tip={t('sidebar:menu.helpCenter')}
                aria-label={t('sidebar:menu.helpCenter')}
                title={t('sidebar:menu.helpCenter')}
              >
                <HelpOutline />
              </a>
              <button
                type="button"
                id="nav-report-bug"
                className="sidebar-help-link-desktop sidebar-report-bug-link sidebar-footer-icon-btn"
                data-sidebar-tip={t('sidebar:menu.reportBug')}
                aria-label={t('sidebar:menu.reportBug')}
                title={t('sidebar:menu.reportBug')}
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  openBugReportFromFooter();
                }}
              >
                <BugReportOutlined />
              </button>
            </div>
          </div>
        </div>
      </nav>
      <BugReportModal isOpen={isBugModalOpen} onClose={() => setIsBugModalOpen(false)} />
    </>
  );
};

export default Sidebar;